import React, {memo, useCallback, useMemo, useState} from 'react';

import './index.scss';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useSelector} from 'react-redux';
import {UserModel} from '../../../../types/control';
import {hasAuthority} from '../../../../utils/role';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {QRScreen} from '../../../QRScreen';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {useRPC} from '../../../../hooks/rpc';
import {useMessage} from '../../../../hooks/message';
import {useLocalization} from "../../../../hooks/localization";
import {useQuestion} from '../../../../hooks/question';

const keyExtractor = (item: UserModel) => item.id;
const classExtractor = (item: UserModel) => item.role;
export const UserListScreen = memo(function UserListScreen() {
    const {t} = useLocalization();
    const users = useSelector(state => state.control.userList);
    const role = useSelector(state => state.ui.role);
    const [rpc] = useRPC();
    const {success, failure} = useMessage();
    const question = useQuestion()

    const [qrVisible, setQrVisible] = useState(false);

    const userList = useMemo(() => users.filter(user => hasAuthority(role, user.role)), [role, users]);
    const currentUsername = useSelector((state) => state.user.username);
    const currentUser = useMemo(() => userList.find((u) => u.username === currentUsername), [currentUsername, userList]);
    const [selectedUser, setSelectedUser] = useState<UserModel>(currentUser ? currentUser : null);
    const currentUsersRole = useSelector(state => state.ui.role)

    const roles = useMemo(() => ({
        'default': t('default'),
        'user': t('user'),
        'admin': t('admin'),
        'service': t('service'),
        'developer': t('developer'),
    }),[t]);
    const displayNameExtractor = useCallback((item: UserModel, column: ColumnModel) => {
        if (column.key === 'role') {
            return t(roles[item.role]) || item.role || '-';
        }
        if (column.key === "is_inactive") {
            return item?.temporary_user?.is_inactive ? t('inactive') : !item?.temporary_user?.is_temporary_user ? "-" : t('active')
        }

        return item[column.key] || '-';
    }, [roles, t]);


    const showQR = useCallback(() => {
        setQrVisible(true);
    }, []);

    const testNotification = useCallback(() => {
        rpc.testNotification(selectedUser.mobile_os, selectedUser.push_token)
            .then(() => success(t('notification_successfully_sent')))
            .catch(failure);
    }, [t, rpc, success, failure, selectedUser]);

    const findRole = useMemo(() => (role === "user" ? true : false), [role])
  
    const onDeleteInactiveUser = useCallback(() => {
        const inactiveUserStatus = userList.some(r => r?.temporary_user?.is_inactive)
        if (!inactiveUserStatus) {
            failure(t("inactive_user_not_found"))
            return;
        }
        question(t("are_you_sure_you_want_to_delete_inactive_users"), () => {
            success(t("inactive_users_successfully_deleted"))
            rpc.deleteInactiveUsers()
        })
    }, [t, question, failure, success, userList, rpc])

    const buttons = useMemo(() => [
        {
            text: t('authority.auth_settings'),
            url: "/settings/users/authority-settings",
            className: 'yellow',
            hidden: findRole,
        },
        {
            text: t('delete_inactive_user'),
            onClick: onDeleteInactiveUser,
            className: 'red',
            hidden: currentUsersRole !== 'user' ? false : true
        },
        {
            text: t('edit'),
            url: `/settings/users/edit/${selectedUser ? selectedUser.id : ''}`,
            className: 'orange',
            hidden: !(selectedUser),
        },
        {
            text: t('notification_test'),
            className: 'red',
            hidden: !(selectedUser) || !(selectedUser?.push_token),
            onClick: testNotification,
        },
        {
            text: t('create_qr'),
            onClick: showQR,
            className: 'blue',
            hidden: !(selectedUser && selectedUser.role !== 'service'),
        },
        {
            text: t('create_user'),
            url: '/settings/users/edit',
            className: 'green',
            hidden: !hasAuthority(role, 'admin'),
        },
    ], [t, role, findRole, selectedUser, showQR, testNotification, onDeleteInactiveUser, currentUsersRole]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('users'),
            key: 'username',
        },
        {
            length: 1,
            name: t('level'),
            key: 'role',
        },
        {
            length: 1,
            name: 'OS',
            key: 'mobile_os',
        },
        {
            length: 2,
            name: 'Push Token',
            key: 'push_token',
        },
        {
            length: 2,
            name: t('temporary_user_status'),
            key: 'is_inactive',
        },
    ], [t]);

    return (
        <>
            <PasswordContainer requiredRole='user' isFullscreen={true}>
                <SettingsPageContainer
                    buttons={buttons}
                    className='user-list-page'
                    backUrl={'/settings'}
                    /*title={t('users')}*/>
                    <SettingsList
                        columns={columns}
                        items={userList}
                        listType='userList'
                        keyExtractor={keyExtractor}
                        classExtractor={classExtractor}
                        displayNameExtractor={displayNameExtractor}
                        selectedItem={selectedUser}
                        onItemSelected={setSelectedUser}
                        notSelectableItems={[{
                            id: 'default',
                            username: t('default_user'),
                            role: 'default',
                            mobile_os: '-',
                            push_token: '-',
                        }]}/>
                </SettingsPageContainer>
            </PasswordContainer>
            {
                renderIf(qrVisible)(
                    () => <QRScreen id={selectedUser.id} onClick={() => setQrVisible(false)}/>,
                )
            }
        </>
    );
});
